/**
 * BeadStringTool.jsx — Cycle 55 · Teach & Explain
 * ─────────────────────────────────────────────────
 * Bead string / Rekenrek. Two rows of 10 (or configurable count/rows)
 * beads split 5-and-5 in classic Rekenrek colours. Tap a bead to slide
 * the group of beads to the "left" (active side) — perfect for
 * counting on, doubles, near doubles, and number bonds.
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";

const clampInt = (v, lo, hi, def) => {
  const n = parseInt(v ?? "", 10);
  if (isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
};

// Each row has `perRow` beads; `active` is the number pushed to the left.
export function BeadStringDisplay({ rows, perRow, active, setActive, readOnly = false }) {
  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="bs-canvas">
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, r) => {
          const a = active[r] || 0;
          return (
            <div key={r} className="flex items-center gap-2" data-testid={`bs-row-${r}`}>
              <div className="w-10 text-right font-heading font-black text-sm tabular-nums pr-1">{a}</div>
              <div className="relative flex-1 min-w-0 h-12 rounded-full border-[3px] border-slate-900 bg-slate-100 px-2 flex items-center overflow-hidden">
                <div className="absolute inset-y-0 left-1/2 w-0.5 bg-slate-900/60 pointer-events-none" />
                <div className="flex-1 min-w-0 flex items-center gap-0.5 sm:gap-1 justify-start">
                  {Array.from({ length: perRow }).map((_, i) => {
                    const idx = i + 1;
                    const isActive = idx <= a;
                    // First half red, second half white — Rekenrek convention.
                    const color = idx <= Math.ceil(perRow / 2) ? "bg-red-400" : "bg-white";
                    const style = isActive ? "left" : "right"; // active pinned left, inactive pushed right
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={readOnly}
                        onClick={() => {
                          if (readOnly) return;
                          // Tapping bead N sets active count to N when N>a, else N-1.
                          const next = [...active];
                          next[r] = isActive ? idx - 1 : idx;
                          setActive(next);
                        }}
                        data-testid={`bs-bead-${r}-${i}`}
                        aria-pressed={isActive}
                        className={`inline-block h-6 w-6 sm:h-8 sm:w-8 rounded-full border-2 border-slate-900 ${color} shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-transform ${isActive ? "" : "opacity-90"}`}
                        style={{ transform: `translateX(${isActive ? 0 : "10px"})` }}
                        aria-label={`Bead ${idx}${isActive ? " active" : ""}`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-center font-heading text-2xl md:text-3xl font-black tabular-nums text-slate-900" data-testid="bs-total">
        Total: {active.reduce((s, n) => s + (n || 0), 0)}
      </p>
    </div>
  );
}

export default function BeadStringTool() {
  const [params, setParams] = useSearchParams();
  const rows   = clampInt(params.get("rows"),   1, 4,   2);
  const perRow = clampInt(params.get("per"),    5, 20, 10);
  const active = (params.get("act") || "0,0").split(",").map((n) => clampInt(n, 0, perRow, 0));
  while (active.length < rows) active.push(0);
  active.length = rows;

  const update = (patch) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };
  const setActive = (next) => update({ act: next.join(",") });

  const settingsPanel = (
    <div className="flex flex-wrap items-center gap-2">
      <label className="text-xs font-bold text-slate-700">Rows
        <input type="number" min="1" max="4" value={rows} data-testid="bs-rows"
               onChange={(e) => update({ rows: e.target.value })}
               className="ml-2 h-9 w-20 px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700">Beads/row
        <input type="number" min="5" max="20" value={perRow} data-testid="bs-perrow"
               onChange={(e) => update({ per: e.target.value })}
               className="ml-2 h-9 w-20 px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
    </div>
  );

  return (
    <ManipulativeFrame
      slug="bead-string"
      title="Bead String (Rekenrek)"
      description="Slide beads to the left in groups. Great for counting on, doubles and number bonds to 10 or 20."
      onReset={() => setActive(Array.from({ length: rows }, () => 0))}
      settingsPanel={settingsPanel}
      shareUrl={window.location.href}
    >
      <BeadStringDisplay rows={rows} perRow={perRow} active={active} setActive={setActive} />
    </ManipulativeFrame>
  );
}
